import { ObservationItem } from '@liza/ui/components/records/ObservationItem';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { useCodeDisplay } from '../../hooks/useCodeDisplay';
import { groupObservations } from '../../utils/observation';

export type VitalsListProps = {
  vitals: WithId<Observation>[];
  onSelectVital?: (vitalIdentifier: string) => void;
  selectedVitalIdentifier?: string;
};

export const VitalsList = ({ vitals, onSelectVital, selectedVitalIdentifier }: VitalsListProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const { displayForResultCode } = useCodeDisplay();

  // Named by the code the row is keyed on, not by the newest reading's own code — a row merging
  // variants of one measurement would otherwise change its label as readings arrive. Uncurated codes
  // (most vitals) take the newest reading's own display text.
  const groupedVitals = groupObservations(vitals).map((g) => ({
    ...g,
    name: displayForResultCode(g.identifier, g.latest.code),
  }));

  if (groupedVitals.length === 0) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <p className="py-8 text-center text-sm text-muted-foreground">{t('noVitalsFound')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      {groupedVitals.map((groupedVital) => (
        <ObservationItem
          key={groupedVital.identifier}
          {...groupedVital}
          onSelect={onSelectVital}
          selected={selectedVitalIdentifier === groupedVital.identifier}
        />
      ))}
    </div>
  );
};
